import type { PackagePricingMode } from '../../types/packages';
import { formatAed } from '../offers/offerUtils';
import { ITEMS_MIN, PERCENT_MAX, PERCENT_MIN, fixedSaving, type PackageFieldErrors } from './packageUtils';
import type { PackagePreviewState } from './usePackagePreview';
import type { PackagesT } from './packagesI18n';

export interface PackagePriceBoxProps {
  t: PackagesT;
  /** The derived mode (preview response when fresh, else the client mirror of D2). */
  mode: PackagePricingMode;
  preview: PackagePreviewState;
  itemCount: number;
  packagePrice: string;
  discountPercent: string;
  disabled?: boolean;
  fieldErrors: PackageFieldErrors;
  onPriceChange: (value: string) => void;
  onPercentChange: (value: string) => void;
}

/**
 * Spec 081b — derived-mode badge plus the one price input that mode needs. FIXED: services total,
 * package price, "Customer saves AED X (Y%)". PERCENT: 5–70 % and the "from AED X" the app shows.
 */
export default function PackagePriceBox(props: PackagePriceBoxProps) {
  const { t, mode, preview, itemCount, packagePrice, discountPercent, disabled, fieldErrors, onPriceChange, onPercentChange } = props;
  const data = preview.data;
  const total = data?.originalTotal ?? null;
  // The server's saving when it priced exactly this form; a local preview while it catches up.
  const saving = preview.fresh && data?.savingAmount != null && data.savingAmount > 0
    ? { amount: data.savingAmount, pct: data.savingPercent ?? 0 }
    : fixedSaving(total, packagePrice);
  const fromPrice = data?.fromPrice ?? null;
  const priceErr = fieldErrors.packagePrice;
  const pctErr = fieldErrors.discountPercent;

  return (
    <div className="pkg-price-box" data-testid="pkg-price-box">
      <div className="pkg-price-box__mode">
        <span className="offer-field-label">{t('packages.price.modeLabel')}</span>
        <span className={`pkg-mode-badge pkg-mode-badge--${mode.toLowerCase()}`} data-testid="pkg-mode-badge">
          {t(`packages.mode.${mode}`)}
        </span>
        {preview.loading && <span className="offer-muted" role="status">{t('packages.price.checking')}</span>}
      </div>
      <p className="offer-field-hint">{t(`packages.price.modeHint.${mode}`)}</p>
      {preview.error && <p className="pkg-warn" role="status">{t('packages.price.previewFailed')}</p>}
      {itemCount < ITEMS_MIN && <p className="offer-muted">{t('packages.price.addServices')}</p>}

      {mode === 'FIXED' ? (
        <div className="offer-grid-2">
          <div className="offer-field">
            <span className="offer-field-label">{t('packages.price.originalTotal')}</span>
            <span className="pkg-price-box__total" dir="ltr" data-testid="pkg-original-total">{total != null ? formatAed(total) : '—'}</span>
          </div>
          <div className="offer-field">
            <label htmlFor="pkg-packagePrice">{t('packages.price.packagePrice')}</label>
            <input id="pkg-packagePrice" type="number" inputMode="decimal" min={0} step="0.01" value={packagePrice} disabled={disabled}
              onChange={(e) => onPriceChange(e.target.value)}
              {...(priceErr ? { 'aria-invalid': true as const, 'aria-describedby': 'pkg-err-packagePrice' } : {})} />
            {priceErr && <div className="offer-field-error" id="pkg-err-packagePrice" role="alert">{priceErr}</div>}
          </div>
          {saving && (
            <p className="pkg-saving" data-testid="pkg-saving">
              {t('packages.price.saves', { amount: formatAed(saving.amount), pct: saving.pct })}
            </p>
          )}
        </div>
      ) : (
        <div className="offer-grid-2">
          <div className="offer-field">
            <label htmlFor="pkg-discountPercent">{t('packages.price.percent')}</label>
            <input id="pkg-discountPercent" type="number" inputMode="numeric" min={PERCENT_MIN} max={PERCENT_MAX} step={1}
              value={discountPercent} disabled={disabled} aria-describedby={pctErr ? 'pkg-err-discountPercent' : 'pkg-percent-hint'}
              onChange={(e) => onPercentChange(e.target.value)} {...(pctErr ? { 'aria-invalid': true as const } : {})} />
            <p className="offer-field-hint" id="pkg-percent-hint">{t('packages.price.percentHint')}</p>
            {pctErr && <div className="offer-field-error" id="pkg-err-discountPercent" role="alert">{pctErr}</div>}
          </div>
          <div className="offer-field">
            {fromPrice != null && (
              <p className="pkg-saving" data-testid="pkg-from-price">{t('packages.price.from', { amount: formatAed(fromPrice) })}</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
